import { useCallback, useEffect, useRef, useState } from 'react'
import { lotApi } from '../utils/lotApi'
import type { LotLineageProjectionRow } from '../types/lot'

/**
 * Fase 3 roster enrichment (tier-a-merge-consumer-mapping.md §2.6): fetches
 * every `LotLineageProjection` row for a farm so screens can fold ancestor
 * lot ids into their roster (`buildLotsByFarmAndKey`, `canonicalLotIdByToken`)
 * without recomputing the ledger walk client-side. Mirrors `useLots.ts`'s
 * per-farm cache shape, but intentionally not written into Dexie — this is a
 * read-time enrichment of an existing roster, not an entity the client owns
 * or syncs (see `lotApi.lineageForFarm`'s doc comment for the offline
 * fail-open behavior this hook inherits).
 */

type CacheEntry = {
  rows: LotLineageProjectionRow[]
  fetchedAt: number
}

const lineageCache = new Map<string, CacheEntry>()

export const invalidateLotLineageProjectionCache = (farmId?: string): void => {
  if (!farmId) {
    lineageCache.clear()
    return
  }
  lineageCache.delete(farmId)
}

type UseLotLineageProjectionOptions = {
  farmId?: string
  enabled?: boolean
}

export const useLotLineageProjection = (options: UseLotLineageProjectionOptions = {}) => {
  const { farmId, enabled = true } = options
  const active = enabled && Boolean(farmId)
  const [rows, setRows] = useState<LotLineageProjectionRow[]>(() => (
    farmId ? lineageCache.get(farmId)?.rows ?? [] : []
  ))
  const [loading, setLoading] = useState<boolean>(active && !lineageCache.has(farmId ?? ''))
  const requestId = useRef(0)

  const refresh = useCallback(async () => {
    if (!active || !farmId) {
      setLoading(false)
      return
    }

    const nextId = requestId.current + 1
    requestId.current = nextId
    setLoading(true)

    const fetched = await lotApi.lineageForFarm(farmId)
    if (requestId.current !== nextId) return
    lineageCache.set(farmId, { rows: fetched, fetchedAt: Date.now() })
    setRows(fetched)
    setLoading(false)
  }, [active, farmId])

  useEffect(() => {
    if (!active || !farmId) {
      setRows([])
      setLoading(false)
      return
    }

    const cached = lineageCache.get(farmId)
    if (cached) {
      setRows(cached.rows)
      setLoading(false)
      return
    }

    void refresh()
  }, [active, farmId, refresh])

  return { rows, loading, refresh }
}
